import { getExistingCache } from '~/services/media/cacheFactory';

/**
 * Quanto da programação está EM DISCO — a métrica que prevê tela preta.
 *
 * <p>O mandato é offline-first: a TV tem que continuar tocando sem rede, e
 * inclusive depois de reiniciar sem rede. Um player com 12 dos 40 vídeos em
 * disco cumpre isso por 12 vídeos e depois apaga. Hoje ninguém vê esse número
 * antes de a loja reclamar.</p>
 *
 * <p><b>Por que existe como módulo:</b> o `health:report` lia o índice do cache
 * direto do localStorage, pela chave do Capacitor
 * (`efflux_capacitor_fs_metadata_v2`). Essa chave só existe no <b>Android</b> —
 * na Tizen é `efflux_tizen_media_metadata_v{N}` e no embedded é
 * `efflux_embedded_media_metadata_v1`. O resultado era `media_cache_files` e
 * `media_cache_mb` chegando <b>null na TV e no embedded</b>, em silêncio, e
 * ninguém percebendo porque no tablet de teste funcionava. A interface
 * `IMediaCache` expõe `getMetadata()` e TODAS as caches implementam — é o
 * caminho que serve as quatro plataformas.</p>
 *
 * <p>🔴 <b>Este módulo não importa store nenhum, e não deve passar a importar.</b>
 * O `SyncService` o consome, e `~/stores` instancia o `syncStore`, que importa o
 * `SyncService` de volta: o ciclo não quebra o build, só entrega `undefined` em
 * runtime — foi o que derrubou 41 testes do `sync.store` quando este arquivo
 * importava `mediaStore` pra contar os itens rastreados. Quem tem o store que
 * passe o número.</p>
 */

export interface CacheDeMidia {
  /** Arquivos efetivamente em disco. */
  files: number | null;
  /** Soma dos tamanhos, em MB. */
  mb: number | null;
}

const VAZIO: CacheDeMidia = { files: null, mb: null };

/**
 * Best-effort absoluto: qualquer falha devolve nulls e o chamador segue.
 *
 * <p>Usa `getExistingCache()`, que NÃO cria cache — se o player ainda não
 * inicializou o armazenamento, a resposta é "não sei", não uma inicialização
 * disparada de dentro do caminho do sync.</p>
 */
export function coletarCacheDeMidia(): CacheDeMidia {
  try {
    const meta = getExistingCache()?.getMetadata() ?? null;
    if (!meta) return VAZIO;

    const entradas = Object.values(meta);
    const bytes = entradas.reduce((t, e) => t + (Number(e?.fileSize) || 0), 0);

    return { files: entradas.length, mb: Math.round(bytes / 1048576) };
  } catch {
    return VAZIO;
  }
}

export default coletarCacheDeMidia;
